/**
 * Builder form ↔ GraphQL input mapping for automation rules (SE-5506).
 *
 * Two responsibilities, kept pure and React-free so they can be asserted
 * directly:
 *   - {@link buildAutomationRuleConditionInputs} — form conditions →
 *     `PipelineAutomationRuleConditionInput[]`, dropping the fields that do not
 *     belong to each condition's value kind and PRESERVING `numericValue` as a
 *     decimal string (never cast to a Float);
 *   - {@link buildCreateAutomationRuleInput} /
 *     {@link buildUpdateAutomationRuleInput} — the whole form → the create /
 *     update input, folding a Guard's implicit trigger/action back onto the
 *     schema shape ({@link classifyAutomationRuleKind}'s inverse).
 *
 * Fields irrelevant to the current trigger/action are omitted rather than sent
 * as null, so an edit never writes a stray stage/tag/outcome the action does
 * not use.
 * @module features/player-kanban/utils/automationRuleInput
 */

import {
  type CreatePipelineAutomationRuleInput,
  PipelineAutomationActionType,
  type PipelineAutomationRuleConditionInput,
  PipelineAutomationTriggerType,
  type UpdatePipelineAutomationRuleInput,
} from "@/generated/graphql";

import type {
  AutomationRuleConditionForm,
  AutomationRuleFormState,
} from "../types/automationRules";
import {
  CONDITION_VALUE_KIND,
  actionNeedsTag,
  actionNeedsTargetStage,
  actionSupportsOutcome,
  triggerNeedsStage,
} from "./automationRuleOptions";

/**
 * Map one form condition to its input, keeping only the fields its value kind
 * uses. `numericValue` is passed through as-is (a decimal string) so the
 * author's exact value survives the round-trip.
 * @param condition - The form condition.
 * @returns The condition input.
 */
const toConditionInput = (
  condition: AutomationRuleConditionForm
): PipelineAutomationRuleConditionInput => {
  const kind = CONDITION_VALUE_KIND[condition.conditionType];

  if (kind === "stage") {
    // Stage-occupancy test — no comparator, no value.
    return {
      conditionType: condition.conditionType,
      ...(condition.kanbanPhaseId
        ? { kanbanPhaseId: condition.kanbanPhaseId }
        : {}),
    };
  }

  if (kind === "text") {
    return {
      conditionType: condition.conditionType,
      ...(condition.comparator ? { comparator: condition.comparator } : {}),
      textValue: condition.textValue,
    };
  }

  // numeric — preserve the decimal string verbatim.
  return {
    conditionType: condition.conditionType,
    ...(condition.comparator ? { comparator: condition.comparator } : {}),
    numericValue: condition.numericValue,
  };
};

/**
 * Map every form condition to inputs.
 * @param conditions - The form conditions.
 * @returns The condition inputs (AND-combined server-side).
 */
export const buildAutomationRuleConditionInputs = (
  conditions: readonly AutomationRuleConditionForm[]
): PipelineAutomationRuleConditionInput[] => conditions.map(toConditionInput);

/**
 * Resolve the effective trigger/action for a form, folding a Guard back onto
 * its on-schema encoding (CARD_ENTERED_STAGE + ADD_TO_REVIEW_QUEUE).
 * @param form - The builder form state.
 * @returns The effective trigger + action.
 */
const effectiveTriggerAction = (
  form: AutomationRuleFormState
): {
  triggerType: PipelineAutomationTriggerType;
  actionType: PipelineAutomationActionType;
} =>
  form.kind === "guard"
    ? {
        triggerType: PipelineAutomationTriggerType.CardEnteredStage,
        actionType: PipelineAutomationActionType.AddToReviewQueue,
      }
    : { triggerType: form.triggerType, actionType: form.actionType };

/**
 * Shared body of the create/update inputs (everything except the id/board).
 * @param form - The builder form state.
 * @returns The shared input fields.
 */
const buildSharedInput = (form: AutomationRuleFormState) => {
  const { triggerType, actionType } = effectiveTriggerAction(form);

  // A Guard always watches (and targets) the chosen stage; an Automation only
  // sets a target when its action uses one.
  const targetStageId =
    form.kind === "guard"
      ? form.triggerKanbanPhaseId
      : form.targetKanbanPhaseId;

  return {
    name: form.name.trim(),
    triggerType,
    actionType,
    conditions: buildAutomationRuleConditionInputs(form.conditions),
    ...(triggerNeedsStage(triggerType) && form.triggerKanbanPhaseId
      ? { triggerKanbanPhaseId: form.triggerKanbanPhaseId }
      : {}),
    ...(actionNeedsTargetStage(actionType) && targetStageId
      ? { targetKanbanPhaseId: targetStageId }
      : {}),
    ...(actionNeedsTag(actionType) && form.kanbanTagId
      ? { kanbanTagId: form.kanbanTagId }
      : {}),
    // An outcome is only meaningful for a MOVE_TO_STAGE action (the builder only
    // captures one when that move targets a terminal stage — see the container's
    // `showOutcome` gate, which clears a stale outcome the moment the target
    // stops being terminal). Gate strictly on the move action so an outcome
    // never leaks onto ADD_TO_REVIEW_QUEUE (a review-queue automation OR a Guard,
    // which folds to that action) even though both also target a stage.
    ...(actionSupportsOutcome(actionType) && form.requestedOutcome
      ? { requestedOutcome: form.requestedOutcome }
      : {}),
    ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
  };
};

/**
 * Build the create input for a new rule.
 * @param form - The builder form state.
 * @param kanbanBoardId - The pipeline the rule governs.
 * @returns The {@link CreatePipelineAutomationRuleInput}.
 */
export const buildCreateAutomationRuleInput = (
  form: AutomationRuleFormState,
  kanbanBoardId: string
): CreatePipelineAutomationRuleInput => ({
  kanbanBoardId,
  ...buildSharedInput(form),
});

/**
 * Build the update input for an existing rule. `kanbanBoardId` is create-only,
 * so it is intentionally absent here.
 * @param form - The builder form state.
 * @param automationRuleId - The rule being edited.
 * @returns The {@link UpdatePipelineAutomationRuleInput}.
 */
export const buildUpdateAutomationRuleInput = (
  form: AutomationRuleFormState,
  automationRuleId: string
): UpdatePipelineAutomationRuleInput => ({
  automationRuleId,
  ...buildSharedInput(form),
});
